import moment from "moment";
import React, { useContext, useState } from "react";
import Modal from "react-modal";
import { NumericFormat } from "react-number-format";
import IAttachment from "../../../../models/IAttachment";
import ICatalog_Item from "../../../../models/ICatalog_Item";
import IOrder from "../../../../models/IOrder";
import CatalogItemService from "../../../../services/CatalogItemService";
import OrderEntryService from "../../../../services/OrderEntryService";
import UIService from "../../../../services/UIService";
import UserService from "../../../../services/UserService";
import ChevronToggle from "../../../shared/ChevronToggle/ChevronToggle";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwS3DownloadLink from "../../../shared/OwS3DownloadLink/OwS3DownloadLink";
import OwTranslate from "../../../shared/OwTranslate/OwTranslate";
import { ItemInventoryByWarehouseModal } from "../../ItemInventoryByWarehouseModal/ItemInventoryByWarehouseModal";
import "./CatalogItemLookupDetails.scss";
import IPriceDetailUI from "../../../../models/IPriceDetailUI";
import { PricePerUnit } from "../../../shared/PricePerUnit/PricePerUnit";
import IDiscount from "../../../../models/IDiscount";
import { QtyOnHandValue } from "../../../shared/QtyOnHandValue/QtyOnHandValue";
import { DescriptionExpander } from "../../../shared/DescriptionExpander/DescriptionExpander";
import { FilesDownloadSection } from "../../../shared/FilesDownloadSection/FilesDownloadSection";
import OwPill from "../../../shared/OwPill/OwPill";
import UserContext from "../../../../contexts/UserContext";
import UiContext from "../../../../contexts/UiContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import VendorContext from "../../../../contexts/VendorContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import { PriceBandPriceDisplay } from "../../../shared/PriceBandPriceDisplay/PriceBandPriceDisplay";
import ProductGroupComponentEnum from "@models/ProductGroupComponentEnum";
import { ClickToCopy } from "@components/shared/ClickToCopy/ClickToCopy";

type Props = {
    isOnline: boolean;
    order: IOrder;
    typeaheadItem?: ICatalog_Item;
    qtyWanted?: number;
    setDiscount: (discount: IDiscount) => void;
};

export const CatalogItemLookupDetails = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const checkHasFiles = (): boolean => {
        let files = false;

        if (props.typeaheadItem?.Attachments) {
            for (const attachment of props.typeaheadItem.Attachments) {
                if (!attachment.image_seq) {
                    files = true;
                }
            }
        }

        return files;
    };

    const [isDetailsVisible, setIsDetailsVisible] = useState(false);
    const [modalComponent, setModalComponent] = useState<any>(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const hasFiles = checkHasFiles();

    const toggleDetails = () => {
        setIsDetailsVisible(!isDetailsVisible);
    };

    const getAttachmentDownloadLink = (catalogItem: ICatalog_Item) => {
        if (catalogItem && catalogItem.Attachments && catalogItem.file_attachment_one_id) {
            let attachmentItem: IAttachment | null = null;

            for (const attachment of catalogItem.Attachments) {
                if (attachment.id === catalogItem.file_attachment_one_id) {
                    attachmentItem = attachment;
                }
            }

            if (attachmentItem) {
                return (
                    <div className={"d-flex"}>
                        <div className={"me-2"}>
                            <OwLabel title={"spec_file"} isBold={true} noBottomMargin={true} />:
                        </div>
                        <OwS3DownloadLink
                            attachment={attachmentItem}
                            leftIconName={"fad fa-download"}
                            title={"download"}
                            isReadOnly={true}
                        />
                    </div>
                );
            } else {
                return null;
            }
        } else {
            return null;
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalComponent(null);
    };

    const viewWarehouseModal = () => {
        const _modalComponent = <ItemInventoryByWarehouseModal closeModalFn={closeModal} catalogItem={props.typeaheadItem!} />;

        setModalComponent(_modalComponent);
        setModalIsOpen(true);
    };

    const getVerticalView = (_pricing: number, _prefix: string, _priceDetail: IPriceDetailUI) => {
        const showInventoryDetails = UserService.showInventoryDetails(supplierModel, userModel);

        const leadTimeDetails = props.typeaheadItem ? OrderEntryService.getCatalogItemLeadTime(props.typeaheadItem) : null;

        let showLeadTime = false;

        let estimatedShipDate: string | null = null;

        let backorderQty: number | null = null;

        const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

        if (props.typeaheadItem?.has_inventory && props.qtyWanted) {
            if (props.typeaheadItem.qty_on_hand === 0) {
                showLeadTime = true;
            }

            if ((props.typeaheadItem?.qty_on_hand || 0) < props.qtyWanted) {
                showLeadTime = true;

                if (props.typeaheadItem.allow_backorder) {
                    backorderQty = Number(props.qtyWanted) - Number(props.typeaheadItem.qty_on_hand || 0);
                }
            }
        }

        if (leadTimeDetails) {
            estimatedShipDate = moment().add(leadTimeDetails.leadTime!, leadTimeDetails.leadTimeType!).format("YYYY-MM-DD");
        }

        let qtyOnHand = props.typeaheadItem?.qty_on_hand;

        if (props.typeaheadItem?.Material_Catalog_Item_Maps?.length) {
            qtyOnHand = CatalogItemService.getItemQtyOnHandBasedOnMaterials(props.typeaheadItem);
        }

        let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

        if (props.order?.Currency) {
            currency = props.order.Currency;
        }

        if (props.typeaheadItem?.is_product_group && supplierModel.group_component === ProductGroupComponentEnum.MODAL) {
            return null;
        }

        return (
            <React.Fragment>
                <tbody>
                    <tr>
                        <td className={"w-10r"}>
                            <OwLabel title={"name"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>{props.typeaheadItem ? props.typeaheadItem.name : null}</td>
                    </tr>
                    <tr>
                        <td>
                            <OwLabel title={"sku"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>
                            <div>{props.typeaheadItem ? props.typeaheadItem.sku : null}</div>
                            <div>
                                {props.typeaheadItem?.is_ordered_on_demand ? (
                                    <OwPill title={"ordered_on_demand"} className={"text-small"} />
                                ) : null}
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <OwLabel title={"upc"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>{props.typeaheadItem ? props.typeaheadItem.upc : null}</td>
                    </tr>
                    {props.typeaheadItem?.short_description?.length ? (
                        <tr>
                            <th>
                                <OwLabel title={"short_description"} />
                            </th>
                            <td className={"pre-wrap"}>{props.typeaheadItem?.short_description}</td>
                        </tr>
                    ) : null}
                    <tr>
                        <td>
                            <OwLabel title={"description"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>
                            {props.typeaheadItem ? (
                                <DescriptionExpander catalogItem={props.typeaheadItem} priceGroup={props.order.Price_Group} />
                            ) : null}
                        </td>
                    </tr>
                    {supplierModel.has_catalog_item_delivery_eta ? (
                        <tr>
                            <td>
                                <OwLabel title={"delivery_eta"} isBold={true} noBottomMargin={true} postText={":"} />
                            </td>
                            <td>{props.typeaheadItem ? UserService.parseDate(props.typeaheadItem.delivery_eta) : null}</td>
                        </tr>
                    ) : null}
                    {!hidePricing && supplierModel.has_map ? (
                        <tr>
                            <td>
                                <OwLabel title={"MAP"} isBold={true} noBottomMargin={true} postText={":"} />
                            </td>
                            <td>
                                <NumericFormat
                                    value={props.typeaheadItem?.map || 0}
                                    displayType={"text"}
                                    thousandSeparator={true}
                                    prefix={prefix}
                                    decimalScale={supplierModel.pricing_precision || 2}
                                    fixedDecimalScale={true}
                                />
                            </td>
                        </tr>
                    ) : null}
                    {hidePricing ? null : (
                        <tr>
                            <td>
                                <OwLabel title={"price_per_unit"} isBold={true} noBottomMargin={true} postText={":"} />
                            </td>
                            <td>
                                {props.typeaheadItem &&
                                props.typeaheadItem.pricing &&
                                props.order.Price_Group &&
                                !props.typeaheadItem.has_price_bands ? (
                                    <PricePerUnit
                                        priceDetail={_priceDetail}
                                        prefix={_prefix}
                                        currency={currency}
                                        exchangeRate={props.order.exchange_rate}
                                        baseCurrency={props.order.Catalog?.Currency}
                                    />
                                ) : null}
                                {props.typeaheadItem &&
                                props.typeaheadItem.pricing &&
                                props.order.Price_Group &&
                                props.typeaheadItem.has_price_bands &&
                                !OrderEntryService.isSumComponentPricedKit(props.typeaheadItem) ? (
                                    <PriceBandPriceDisplay
                                        bands={(props.typeaheadItem.Price_Bands || {})[props.order.Price_Group.value]}
                                        currency={props.order.Catalog?.Currency}
                                    />
                                ) : null}
                            </td>
                        </tr>
                    )}
                    {hidePricing ? null : !supplierModel.has_qty_break ? null : (
                        <>
                            <tr>
                                <td>
                                    <OwLabel title={"qty_break"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>
                                    {props.typeaheadItem && props.typeaheadItem.qty_break && props.order.Price_Group ? (
                                        <NumericFormat
                                            value={props.typeaheadItem.qty_break}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                        />
                                    ) : (
                                        "N/A"
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <OwLabel title={"qty_break_price"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>
                                    {props.typeaheadItem && props.typeaheadItem.qty_break_price && props.order.Price_Group ? (
                                        <NumericFormat
                                            value={props.typeaheadItem.qty_break_price}
                                            displayType={"text"}
                                            thousandSeparator={true}
                                            prefix={_prefix}
                                            decimalScale={supplierModel.pricing_precision || 2}
                                            fixedDecimalScale={true}
                                        />
                                    ) : (
                                        "N/A"
                                    )}
                                </td>
                            </tr>
                        </>
                    )}
                    <tr>
                        <td>
                            <OwLabel title={"uom"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>
                            {props.typeaheadItem && props.typeaheadItem.Unit_Of_Measurement
                                ? props.typeaheadItem.Unit_Of_Measurement.name
                                : null}
                        </td>
                    </tr>
                    {props.typeaheadItem && props.typeaheadItem.has_inventory ? (
                        <React.Fragment>
                            <tr>
                                <td>
                                    <OwLabel title={"qty_on_hand"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                {showInventoryDetails ? (
                                    <td>
                                        <div>
                                            <QtyOnHandValue
                                                catalogItem={props.typeaheadItem}
                                                mapItem={qtyOnHandMapItem}
                                                shippingWarehouse={props.order.Shipping_Warehouse}
                                            />
                                        </div>
                                        {props.typeaheadItem.Warehouse_Catalog_Items_Maps?.length ? (
                                            <div>
                                                <OwLabel title={"warehouses"} fn={viewWarehouseModal} />
                                            </div>
                                        ) : null}
                                    </td>
                                ) : (
                                    <td>{props.typeaheadItem.qty_on_hand ? (props.typeaheadItem.qty_on_hand > 0 ? "Y" : "N") : "--"}</td>
                                )}
                            </tr>
                            {showInventoryDetails ? (
                                <tr>
                                    <td>
                                        <OwLabel title={"qty_incoming"} isBold={true} noBottomMargin={true} postText={":"} />
                                    </td>
                                    <td>{props.typeaheadItem.qty_incoming}</td>
                                </tr>
                            ) : null}
                        </React.Fragment>
                    ) : null}
                    {props.typeaheadItem && props.typeaheadItem.has_inventory ? (
                        <React.Fragment>
                            <tr>
                                <td>
                                    <OwLabel title={"allow_backorders"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>{props.typeaheadItem.allow_backorder ? <i className={"fas fa-check text-success"} /> : "--"}</td>
                            </tr>
                        </React.Fragment>
                    ) : null}
                    {props.typeaheadItem?.has_inventory && leadTimeDetails && showLeadTime ? (
                        <React.Fragment>
                            {showInventoryDetails ? (
                                <tr>
                                    <td>
                                        <OwLabel title={"backorder_qty"} isBold={true} noBottomMargin={true} postText={":"} />
                                    </td>
                                    <td>{backorderQty}</td>
                                </tr>
                            ) : null}
                            {leadTimeDetails.leadTimeType ? (
                                <tr>
                                    <td>
                                        <OwLabel title={"lead_time"} isBold={true} noBottomMargin={true} postText={":"} />
                                    </td>
                                    <td>
                                        {leadTimeDetails.leadTime}
                                        <span className={"ms-1"}>
                                            <OwTranslate defaultMessage={""} id={leadTimeDetails.leadTimeType} />
                                        </span>
                                    </td>
                                </tr>
                            ) : null}
                            {estimatedShipDate ? (
                                <tr>
                                    <td>
                                        <OwLabel title={"est_ship_date"} isBold={true} noBottomMargin={true} postText={":"} />
                                    </td>
                                    <td>{estimatedShipDate}</td>
                                </tr>
                            ) : null}
                            {(supplierModel.Supplier_Integration.QBD_Custom_Fields || [])
                                .filter((item) => item.display_only)
                                .map((item, index) => {
                                    // @ts-expect-error
                                    const displayItem = (props.typeaheadItem.display_fields || []).find(
                                        (displayField) => item.name === displayField.name
                                    );
                                    return (
                                        <tr key={item.guid}>
                                            <th>{item.name}</th>
                                            <td>{displayItem?.value}</td>
                                        </tr>
                                    );
                                })}
                        </React.Fragment>
                    ) : null}
                    {supplierModel.has_dimensions && (
                        <React.Fragment>
                            <tr>
                                <td>
                                    <OwLabel title={"width"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>
                                    {props.typeaheadItem?.width} {props.typeaheadItem?.Dimension_Unit?.abbreviation}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <OwLabel title={"height"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>
                                    {props.typeaheadItem?.height} {props.typeaheadItem?.Dimension_Unit?.abbreviation}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <OwLabel
                                        title={supplierModel.length_for_depth ? "length" : "depth"}
                                        isBold={true}
                                        noBottomMargin={true}
                                        postText={":"}
                                    />
                                </td>
                                <td>
                                    {props.typeaheadItem?.depth} {props.typeaheadItem?.Dimension_Unit?.abbreviation}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <OwLabel title={"case_width"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>
                                    {props.typeaheadItem?.case_width} {props.typeaheadItem?.Case_Dimension_Unit?.abbreviation}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <OwLabel title={"case_height"} isBold={true} noBottomMargin={true} postText={":"} />
                                </td>
                                <td>
                                    {props.typeaheadItem?.case_height} {props.typeaheadItem?.Case_Dimension_Unit?.abbreviation}
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <OwLabel
                                        title={supplierModel.length_for_depth ? "case_length" : "case_depth"}
                                        isBold={true}
                                        noBottomMargin={true}
                                        postText={":"}
                                    />
                                </td>
                                <td>
                                    {props.typeaheadItem?.case_depth} {props.typeaheadItem?.Case_Dimension_Unit?.abbreviation}
                                </td>
                            </tr>
                        </React.Fragment>
                    )}
                    <tr>
                        <td>
                            <OwLabel title={"item_weight"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>
                            {props.typeaheadItem?.item_weight} {props.typeaheadItem?.Item_Weight_Unit?.abbreviation}
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <OwLabel title={"shipping_weight"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>
                            {props.typeaheadItem?.shipping_weight} {props.typeaheadItem?.Shipping_Weight_Unit?.abbreviation}
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <OwLabel title={"case_weight"} isBold={true} noBottomMargin={true} postText={":"} />
                        </td>
                        <td>
                            {props.typeaheadItem?.case_weight} {props.typeaheadItem?.Case_Weight_Unit?.abbreviation}
                        </td>
                    </tr>
                </tbody>
            </React.Fragment>
        );
    };

    let pricing = 0;
    let priceDetail: IPriceDetailUI | null = null;

    if (props.typeaheadItem && props.typeaheadItem.pricing && props.order.Price_Group) {
        priceDetail = OrderEntryService.getDiscountedPriceForCatalogItem(props.typeaheadItem, supplierModel, props.order);

        pricing = priceDetail.discountedPrice;
    }

    let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

    if (props.order?.Currency) {
        currency = props.order.Currency;
    }

    const prefix = currency ? OrderEntryService.getCurrencySymbol(currency.name) : "$";

    let tableClass = "table-dark";

    if (UIService.isMainViewLight()) {
        tableClass = "";
    }

    const _showInventoryDetails = UserService.showInventoryDetails(supplierModel, userModel);

    const qtyOnHandMapItem =
        props.order.Shipping_Warehouse && props.typeaheadItem
            ? CatalogItemService.getQtyOnHandWarehouseMapItem(props.typeaheadItem, props.order.Shipping_Warehouse)
            : undefined;

    const hasAttributes = !!props.typeaheadItem?.Catalog_Item_Attributes?.length;

    return (
        <div className={"CatalogItemLookupDetails w-100"}>
            <div className={"accordion-header"} onClick={toggleDetails}>
                <ChevronToggle showChevron={true} isVisible={isDetailsVisible} toggleFn={() => {}} className={"main-text-color me-2"} />
                <div className={"d-flex me-auto"}>
                    <OwLabel title={"additional_information"} className={"me-2"} />

                    <span className={"ms-2"}>
                        {hasFiles && (
                            <OwPill title={"files"} className={"bg-secondary ps-2 pe-2 me-2"} leftIconClass={"fas fa-file-lines"} />
                        )}
                        <OwPill title={"details"} className={"bg-secondary ps-2 pe-2 me-2"} leftIconClass={"fas fa-info-circle"} />
                        {hasAttributes && (
                            <OwPill title={"attributes"} className={"bg-secondary ps-2 pe-2 me-2"} leftIconClass={"fas fa-cog"} />
                        )}
                    </span>

                    {_showInventoryDetails && props.typeaheadItem?.has_inventory ? (
                        <span className={"d-inline-flex me-auto"}>
                            <OwLabel title={"qoh"} postText={":"} className={"me-2"} noBottomMargin={true} />
                            <QtyOnHandValue
                                catalogItem={props.typeaheadItem}
                                mapItem={qtyOnHandMapItem}
                                shippingWarehouse={props.order.Shipping_Warehouse}
                            />
                        </span>
                    ) : null}

                    {uiContext.showHiddenFeatures && props.typeaheadItem ? (
                        <div className="d-flex">
                            <div>
                                <b>ID:</b>
                                <ClickToCopy text={props.typeaheadItem.id.toString()} />
                            </div>
                            <div>
                                <b>GUID:</b>
                                <ClickToCopy text={props.typeaheadItem.guid} />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
            {isDetailsVisible ? (
                <div className={"details mt-1"}>
                    {hasFiles && props.typeaheadItem ? (
                        <div>
                            <FilesDownloadSection catalogItem={props.typeaheadItem} readOnly={true} className={"text-small"} />
                        </div>
                    ) : null}
                    <div>
                        <OwLabel title={"additional_details"} isBold={true} className={"mt-2"} />
                    </div>
                    <table className={tableClass + " table table-sm table-striped " + (isDetailsVisible ? "" : "d-none")}>
                        {getVerticalView(pricing, prefix, priceDetail!)}
                    </table>
                    {props.typeaheadItem?.Catalog_Item_Attributes?.length ? (
                        <div>
                            <OwLabel title={"attributes"} isBold={true} className={"mt-2"} />
                            <table className={tableClass + " table table-sm table-striped " + (isDetailsVisible ? "" : "d-none")}>
                                <tbody>
                                    {props.typeaheadItem?.Catalog_Item_Attributes?.map((attribute) => {
                                        return (
                                            <tr key={attribute.id}>
                                                <th className={"w-10r"}>{attribute.Attribute?.name}</th>
                                                <td>{attribute.value}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : null}
                    {uiContext.showHiddenFeatures ? (
                        <div>
                            <div className="mt-2">
                                <u>Catalog Item</u>
                            </div>
                            <div className="d-flex align-items-center">
                                Catalog Item ID:{" "}
                                <ClickToCopy text={props.typeaheadItem?.id?.toString() || ""} noFlexGrow={true} className="ms-2" />
                            </div>
                            <div>QoH: {props.typeaheadItem?.qty_on_hand}</div>
                            <div>Reserved: {props.typeaheadItem?.reserved_qty_on_hand}</div>
                            <div>Backordered: {props.typeaheadItem?.qty_backordered}</div>
                            <div className="mt-2">Allow Backorder: {props.typeaheadItem?.allow_backorder ? "Yes" : "No"}</div>
                            <div>Max Backorder Limit: {props.typeaheadItem?.backorder_limit}</div>
                        </div>
                    ) : null}
                </div>
            ) : null}

            <div>
                <Modal
                    isOpen={modalIsOpen}
                    contentLabel="Orderwerks"
                    shouldCloseOnOverlayClick={false}
                    className={"modalContentClass"}
                    overlayClassName={"modalOverlayClass"}>
                    {modalComponent}
                </Modal>
            </div>
        </div>
    );
};
